import { generateApiErrObj } from 'client/hooks/useClientErrorHandler';
import { toLowerCase } from 'utils/manipulateString';
import * as clientErrTypes from 'config/clientErrorTypes';
const errObjTypes = {
    CHUNK_LOAD_ERROR: 'chunkloaderror',
    TYPE_ERROR: 'typeerror',
    ABORT_ERROR: 'aborterror'
};

export function isOneOfErrors(checkers = [], errObj) {
    return checkers.some(c => c(errObj));
}
export function isChunkLoadError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errCode = toLowerCase(errObj.code);
    const errMessage = toLowerCase(errObj.message);
    return [
        errName === errObjTypes.CHUNK_LOAD_ERROR,
        errCode === 'css_chunk_load_failed',
        errMessage.includes('loading chunk')
    ].some(e => e);
}
export function isWebKitError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return errName === errObjTypes.TYPE_ERROR && [errMessage.startsWith('webkit')].some(e => e);
}
export function isDNSLookupError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.TYPE_ERROR &&
        [errMessage.includes('無法找到指定主機名稱的伺服器')].some(e => e)
    );
}
export function isSSLConnectionError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.TYPE_ERROR &&
        [errMessage.includes('發生ssl錯誤，無法建立與伺服器的安全連線')].some(e => e)
    );
}
export function isFetchNetworkError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.ABORT_ERROR ||
        (errName === errObjTypes.TYPE_ERROR &&
            [
                errMessage === '已取消',
                errMessage === 'cancelled',
                errMessage === '要求逾時。',
                errMessage === 'the request timed out.',
                errMessage === 'network request failed',
                errMessage === 'the network connection was lost.',
                errMessage === '網路連線中斷。',
                errMessage === 'internet 連線已斷開。',
                errMessage === 'internet連線已斷開。',
                errMessage === '無法完成作業。軟體導致連線失敗',
                errMessage === 'networkerror when attempting to fetch resource.',
                errMessage === '無法連接伺服器。'
            ].some(e => e))
    );
}
export function isFailedToLoadError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return errName === errObjTypes.TYPE_ERROR && errMessage === 'load failed';
}
export function isFailedToFetchError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return errName === errObjTypes.TYPE_ERROR && errMessage === 'failed to fetch';
}
export function isPreFlightError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.TYPE_ERROR && errMessage === 'preflight response is not successful'
    );
}
export function isCORSDueToServerNoResponse(errObj = {}) {
    // 當 server 無法回應時，包括無法回出正確 header 時，因為 header 沒有 allow origin 瀏覽器會當作 cors
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.TYPE_ERROR &&
        errMessage.includes('is not allowed by access-control-allow-origin')
    );
}
// Due to long term expired
export function isUnauthorizedToGetAccessToken(errObj = {}) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 401 && message === 'unauthorized.';
}
export function isFeverApiAccessTokenExpired(errObj = {}) {
    const { code } = normalizeApiErrInfo(errObj);
    return code === 'maj-r1-fu1';
}
export function isFormApiDuplicateSubmitFormError(errObj = {}) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 422 && message === '2-1003-0001-21';
}
export function isFormApiAccessTokenExpired(errObj = {}) {
    const { message, code } = normalizeApiErrInfo(errObj);
    return message === '1-3003-1004-22' && code === '401';
}
export function isGetWinnerEmptySuspendError(errObj) {
    const { status, code } = normalizeApiErrInfo(errObj);
    // 排除 uuid 帶錯：pm-v0-f0 這組碼底下後端至少有「Promotion status is Pause」
    // 「UUID is error」等多種 message，而 uuid 帶錯是前端把 promo 與 entry 配錯，
    // 不是活動狀態問題。不排除的話它會被當成「獎品發完要暫停活動」，顯示
    //「活動未開放」並把 client 的 gameRunningStatus 標成 suspend——一個 client bug
    // 就此偽裝成活動被暫停，沒人查得到（#86eyfdna9）。
    return status === 400 && code === 'pm-v0-f0' && !isEntryPromoUuidMismatchError(errObj);
}
/**
 * promo 與 entry 分屬不同活動：後端 validation/lotteries 的第一步
 *「確認 promotionUuid 與 Entry 所屬活動一致」不成立。純屬 client 端配對錯誤。
 */
export function isEntryPromoUuidMismatchError(errObj) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'pm-v0-f0' && message === 'uuid is error';
}
export function isGetWinnerDuplicateError(errObj) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'etf-v0-f0' && message === 'entry flow - already exists';
}
export function isGetWinnerEmptyContinueError(errObj) {
    const { status, code } = normalizeApiErrInfo(errObj);
    return status === 226 && code === 'lt-v0-f0';
}
export function isGetWinnerWhenKKboxOrderUnfulfillErr(errObj) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'etf-v0-f0' && message === 'kkpoint order - entry flow';
}
export function isFormApiSetGameResultWhenSuspendErr(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 422 && message === '3-0001-0301-31';
}
export function isRequestAtInvalidPromoTime(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 403 && message === 'it is not time now';
}
export function isErrorWhenPromoSuspend(errObj) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'pm-v0-f0' && message === 'promotion status is pause';
}
export function isErrorWhenPromoEnd(errObj) {
    const { status, message, code } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'cudf-v0-f0' && message === 'promotion is over';
}
export function isErrOfGetEntryWhenPromoInvalid(errObj = {}) {
    // 非上線狀態、或活動時間未開放
    const { code, message } = normalizeApiErrInfo(errObj);
    return code === 'cudf-au0-f0' && message === 'promotion is invalid.';
}

export function isErrOfGetEntryWhenReCaptchaMissing(errObj = {}) {
    // 啟用 reCaptcha 但沒有帶入 reCaptcha token
    const { code, message } = normalizeApiErrInfo(errObj);
    return code === 'rcpa-au0-f0' && message === 'recaptcha token authorize failed - empty';
}

export function isErrOfRecaptchaInvalidInput(errObj = {}) {
    // Cloudflare 金鑰無效，活動被後端自動暫停
    const { code, message } = normalizeApiErrInfo(errObj);
    return code === 'rcpa-au0-f0' && message === 'recaptcha token authorize failed - invalid input';
}

export function isErrOfCyberbizEcLimit(errObj = {}) {
    const {
        code,
        data: { failed_type },
        message
    } = normalizeApiErrInfo(errObj);
    // 完整 message 為 "MemberCyberbiz - member accessibility not allowed"
    return (
        code === 'cudf-v0-f0' &&
        failed_type === 'qualification' &&
        message.includes('membercyberbiz')
    );
}

// 新會員註冊
export function isErrOfNewRegisterMemberLimit(errObj = {}) {
    const {
        code,
        data: { failed_type },
        message
    } = normalizeApiErrInfo(errObj);
    // 完整 message 為 "RegistrationDate - member accessibility not allowed"
    return (
        code === 'cudf-v0-f0' &&
        failed_type === 'qualification' &&
        message.includes('registrationdate')
    );
}
export function isErrOfMemberTier(errObj = {}) {
    const {
        code,
        data: { failed_type },
        message
    } = normalizeApiErrInfo(errObj);
    // 完整 message 為 "MemberTier - member accessibility not allowed"
    return (
        code === 'cudf-v0-f0' && failed_type === 'qualification' && message.includes('membertier')
    );
}

// 成就等級限制
export function isErrOfAchievementLevel(errObj = {}) {
    const {
        code,
        data: { failed_type },
        message
    } = normalizeApiErrInfo(errObj);
    // 完整 message 為 "Achievement member level not allowed"

    return (
        code === 'qacmlv-v0-f0' &&
        failed_type === 'qualification' &&
        message === 'achievement member level not allowed'
    );
}
export function isErrOfApiNameListLimit(errObj = {}) {
    const {
        code,
        data: { failed_type },
        message
    } = normalizeApiErrInfo(errObj);
    // 完整 message 為 "RestrictedList - member accessibility not allowed"
    return (
        code === 'cudf-v0-f0' &&
        failed_type === 'qualification' &&
        message.includes('restrictedlist')
    );
}
export function isErrOfMemberOrderLimit(errObj = {}) {
    const {
        code,
        data: { failed_type },
        message
    } = normalizeApiErrInfo(errObj);
    // 完整 message 為 "OrderList - member accessibility not allowed"
    return (
        code === 'cudf-v0-f0' && failed_type === 'qualification' && message.includes('orderlist')
    );
}

// 會員綁定檢查
export function isErrOfMemberBind(errObj = {}) {
    const {
        status,
        code,
        data: { failed_type, redirectUrl },
        message
    } = normalizeApiErrInfo(errObj);

    const isEnterpriseBindLineError =
        status === 401 &&
        code === 'cudf-v0-f0' &&
        failed_type === 'qualification' &&
        message === 'enterprisebonded - member accessibility not allowed';

    const isLineMemberBindWithinDaysError =
        status === 400 && code === 'malbb-3rd-f0' && message === 'qualification link days failed';

    const isBotBonnieMemberNotLinkedError =
        status === 400 &&
        code === 'malbb-3rd-f0' &&
        message === 'member not have link account' &&
        Boolean(redirectUrl);

    const isMAACMemberNotLinkedError =
        status === 400 &&
        code === 'malmaac-3rd-f0' &&
        message === 'member not found in maac, or member not have link account' &&
        Boolean(redirectUrl);

    const isSettingNotFound =
        status === 400 &&
        code === 'cudf-v0-f0' &&
        message === 'can not found setting - there is no matched result in config';

    return (
        isEnterpriseBindLineError ||
        isLineMemberBindWithinDaysError ||
        isBotBonnieMemberNotLinkedError ||
        isMAACMemberNotLinkedError ||
        isSettingNotFound
    );
}
// OCEP 會員 × LINE 綁定（#86ey77cy4）：qualification/verify 於 ocep 平台未綁定時
// 回 HTTP 422 資格不符（throwExceptionWhenVerifyFailed(422)），payload 只有
// failed_type: 'qualification'、不帶 redirectUrl（與 maac/botbonnie 的 400 + redirectUrl 區隔）。
// 後端規格：prompt-library backend/spec/member-account-link/spec.md §3.1.1、
// 20260706-brita-binding-mapping/test-list-promotion-api.md TC-052（2026-07-15 定案）
export function isErrOfMemberBindOcep(errObj = {}) {
    const { status, data: { failed_type: failedType, redirectUrl } = {} } = normalizeApiErrInfo(
        errObj
    );
    return status === 422 && failedType === 'qualification' && !redirectUrl;
}

export function isEntriesOverdueError(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 403 && message === 'entry overdue';
}
export function isGetEntriesLimitError(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 400 && message === 'entry total limit - validate failed';
}

// 方案人次已滿 - https://app.clickup.com/t/86eptk8a0
export function isCreateEntryTotalMonthlyEntryLimitError(errObj) {
    const { message, code } = normalizeApiErrInfo(errObj);
    // form 的 API 因為是舊專案的錯誤處理，所以 code 會是 400，目前後端表示不好統ㄧ改成 ent-v0-f0
    return (
        Boolean(code === 'ent-v0-f0' || Number(code) === 400) &&
        message === 'total monthly entry limit - validate failed'
    );
}
export function isGetEntriesPlayTimeLimitErr(errObj) {
    const { status, message, code } = normalizeApiErrInfo(errObj);
    // ent-v0-f0 是 pkg 整合產生出的 code
    return (
        status === 403 &&
        (code === 'et-v0-f0' || code === 'ent-v0-f0') &&
        ['totalplaytimes', 'dailyplaytimes', 'period time data exists'].includes(message)
    );
}
export function isSetGameResultWhenContestModuleRecalculatingRank(errObj) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'vt-c0-f0' && message.startsWith('please wait a moment');
}
export function isUgcVoteDuplicate(errObj) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return status === 500 && code === 'vt-c0-f0' && message === 'vote failed';
}
export function isVoteOnNoSignupFormUgc(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 400 && message === 'media library not already';
}
export function isVoteOnNotQualifiedUgc(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 400 && message === 'media library validate failed';
}
export function isVoteLimitError(errObj) {
    const { status, message, code } = normalizeApiErrInfo(errObj);
    // ent-v0-f0 是 pkg 整合產生出的 code
    return (
        status === 403 &&
        (code === 'et-v0-f0' || code === 'ent-v0-f0') &&
        message === 'vote times validate failed'
    );
}
export function isLineLoginCredentialErr(errObj) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return (
        status === 400 && code === 'cudf-3rd-f0' && message === 'line friendship status api error'
    );
}
export function isTooManyAttemptErr(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 429 && message === 'too many attempts.';
}
export function isErrPromoSubmitRecaptchaExpired(errObj) {
    const { status, message, code } = normalizeApiErrInfo(errObj);
    return (
        status === 400 && code === 'rcpa-au0-f0' && message === 'recaptcha token authorize failed'
    );
}
export function isErrCreateEntriesTimeOut(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 500 && message.includes('operation timed out');
}
export function isErrMemberPointsNotEnough(errObj) {
    const { status, message, code, data } = normalizeApiErrInfo(errObj);
    return (
        (status === 400 && code === 'psm-v0-f0' && message === 'member points is not enough') ||
        // 會員點數走 uTagGo 扣點時，餘額不足會回傳 UTGO-3RD-F0 + providerCode A6101，
        // 與本系統 (PSM) 餘額不足視為相同錯誤，讓後續流程行為一致
        (status === 400 && code === 'utgo-3rd-f0' && data?.providerCode === 'A6101')
    );
}

export function isErrOfSequentialTaskNotUnlocked(errObj = {}) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return (
        status === 400 &&
        code === 'et-v0-f0' &&
        message === 'sequential task not unlocked - previous task not completed'
    );
}

export function isErrOfTagLimit(errObj = {}) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return (
        status === 400 &&
        (message === 'user have exclude tag' || message === 'user not have group tag')
    );
}

// 由外部 API 控制參加
export function isErrOfExternalApiNotAllowed(errObj = {}) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'pms-v0-f0' && message === 'accessibility not allowed';
}

export function isPromoRelatedAndCanNotUpdate(errObj = {}) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 400 && message === 'this promotion be related to other promtoion';
}

export function isTurnstileVerificationFailedResp(errObj = {}) {
    const { status, code } = normalizeApiErrInfo(errObj);
    return status === 403 && code === 'pmm-au0-f0';
}

/**
 * uTagGo 第三方點數系統錯誤 envelope 分類。
 * 外層 code 為本系統 ResponseCode（UTGO-3RD-F0 / UTGO-V0-F0），
 * 對方（uTagGo）回傳的原因放在 payload.data：
 *   - data.httpStatus 200 → 對方有回應但業務拒絕（code≠0000，如無帳戶 A6201；餘額不足 A6101 另由 isErrMemberPointsNotEnough 處理）
 *   - data.httpStatus 5xx/null → 對方 HTTP 失敗（逾時或掛掉）
 *   - data.httpStatus 401 → token 失效，刷新後仍失效（需引導會員重新登入）
 * 分為兩類（見 useClientErrorHandler）：
 *   - isUTagGoTokenError：token 失效 → 顯示重新登入彈窗，不送 Sentry
 *   - isUTagGoExceptionError：非餘額不足的業務拒絕 / HTTP 失敗 / 參數驗證失敗
 *       · 有對方原因碼 (providerCode) → 顯示系統錯誤並帶出該碼，不送 Sentry
 *       · 無 providerCode → 送 Sentry 並顯示一般錯誤（帶 Sentry eventId）
 *   （餘額不足 A6101 由 isErrMemberPointsNotEnough 統一處理）
 * @see prompt-library/backend/Api/promotion-api/{transactionWithdraw,getMemberPoint,redeemPrizeCostPoint}.yaml
 */
const UTGO_THIRD_PARTY_CODE = 'utgo-3rd-f0';
const UTGO_VALIDATE_CODE = 'utgo-v0-f0';

// token 失效，刷新後仍失效（對方或外層回 401，需引導會員重新登入）
export function isUTagGoTokenError(errObj = {}) {
    const { code, status, data } = normalizeApiErrInfo(errObj);
    return code === UTGO_THIRD_PARTY_CODE && (status === 401 || data?.httpStatus === 401);
}

// uTagGo 其餘例外：非餘額不足的業務拒絕（httpStatus 200）/ 第三方 HTTP 失敗（5xx/逾時/null）/ 參數驗證失敗
export function isUTagGoExceptionError(errObj = {}) {
    const { code, status, data } = normalizeApiErrInfo(errObj);
    if (code === UTGO_VALIDATE_CODE) return true;
    // 第三方錯誤但非 token 失效（餘額不足 A6101 由 isErrMemberPointsNotEnough 另外處理）
    return code === UTGO_THIRD_PARTY_CODE && status !== 401 && data?.httpStatus !== 401;
}

export function normalizeApiErrInfo(errObj = {}) {
    const { method, httpStatus, payload: { code, data, message } = {} } =
        errObj.extraInfo ?? errObj;
    return {
        method,
        status: httpStatus,
        data: data ?? {},
        code: String(code).toLowerCase(),
        message: String(message).toLowerCase()
    };
}

export const feverErrCodes = {
    FAILED_TO_FETCH: 'fv-g-ff-001',
    FAILED_TO_LOAD: 'fv-g-fl-001',
    ERR_PREFLIGHT: 'fv-g-pf-001',
    FAILED_RESP_CORS: 'fv-g-sfc-001',
    ERR_DNS_LOOK_UP: 'fv-g-dl-001',
    ERR_SSL_CONNECT: 'fv-g-ssl-001',
    ERR_WEBKIT: 'fv-g-wk-001',
    ERR_DUPLICATE_GET_WINNER: 'fv-p-gw-001',
    ERR_FORM_DUPLICATE: 'fv-f-001',
    ERR_CREATE_ENTRIES_TIMEOUT: 'fv-ce-001'
};

export function getGeneralErrCode(errObj) {
    switch (true) {
        case isFailedToFetchError(errObj):
            const failedToFetchError = generateApiErrObj(errObj, clientErrTypes.FAILED_TO_FETCH);

            return {
                code: feverErrCodes.FAILED_TO_FETCH,
                error: failedToFetchError,
                isShouldSendSentry: false
            };
        case isFailedToLoadError(errObj):
            const failedToLoadError = generateApiErrObj(errObj, clientErrTypes.FAILED_TO_LOAD);

            return {
                code: feverErrCodes.FAILED_TO_LOAD,
                error: failedToLoadError
            };
        case isPreFlightError(errObj):
            const preflightResponseIsNotSuccessfulError = generateApiErrObj(
                errObj,
                clientErrTypes.PREFLIGHT_RESPONSE_IS_NOT_SUCCESSFUL
            );

            return {
                code: feverErrCodes.ERR_PREFLIGHT,
                error: preflightResponseIsNotSuccessfulError
            };
        case isCORSDueToServerNoResponse(errObj):
            const notAllowedByAccessControlAllowOriginError = generateApiErrObj(
                errObj,
                clientErrTypes.NOT_ALLOWED_BY_ACCESS_CONTROL_ALLOW_ORIGIN
            );

            return {
                code: feverErrCodes.FAILED_RESP_CORS,
                error: notAllowedByAccessControlAllowOriginError
            };
        case isDNSLookupError(errObj):
            const dnsLookUpFailedError = generateApiErrObj(
                errObj,
                clientErrTypes.DNS_LOOK_UP_FAILED
            );

            return {
                code: feverErrCodes.ERR_DNS_LOOK_UP,
                error: dnsLookUpFailedError
            };
        case isSSLConnectionError(errObj):
            const sslConnectionError = generateApiErrObj(
                errObj,
                clientErrTypes.SSL_CONNECTION_ERROR
            );

            return {
                code: feverErrCodes.ERR_SSL_CONNECT,
                error: sslConnectionError
            };
        case isWebKitError(errObj):
            const webKitError = generateApiErrObj(errObj, clientErrTypes.WEB_KIT_ERROR);

            return {
                code: feverErrCodes.ERR_WEBKIT,
                error: webKitError
            };
        default:
            return {};
    }
}

export const highTrafficErrCodes = new Set([
    feverErrCodes.FAILED_TO_FETCH,
    feverErrCodes.FAILED_TO_LOAD,
    feverErrCodes.ERR_PREFLIGHT,
    feverErrCodes.FAILED_RESP_CORS
]);
